import { REACT_APP_API_TOKEN } from '@env';
import { Platform } from 'react-native';

import { getAuthService } from './auth/authServiceFactory';
import { getApiConfig } from '../config/api';

// Use centralized API configuration with device detection
// IMPORTANT: Use lazy getter to avoid calling native modules at module load time
// Calling DeviceInfo.isEmulatorSync() at module load can crash iOS
let _cachedApiBaseUrl: string | null = null;
/**
 * ⚠️ EXPORTED for #1069 Story 4.1's upload transport
 * (`src/services/correspondence/postWithProgress.ts`), which needs a real
 * `XMLHttpRequest` on EVERY platform for honest progress (AC 9) and an abort
 * (AC 11), neither of which this module offers on native. It must resolve the
 * base URL through this same cached getter rather than re-deriving it: two
 * spellings of "where is the API" is how one path silently talks to the wrong
 * host. Not a general-purpose export; do not reach for it to avoid `api`.
 */
export const getResolvedApiBaseUrl = (): string => {
  if (_cachedApiBaseUrl === null) {
    const url = getApiConfig().BASE_URL;
    if (__DEV__) {
      console.log('🔍 API Service: Using BASE_URL:', url);
    }
    _cachedApiBaseUrl = `${url.replace(/\/$/, '')}/api`;
  }
  return _cachedApiBaseUrl;
};

/**
 * The headers every API call carries: content type, the environment's API
 * token when one is configured, and the caller's JWT.
 *
 * ⚠️ EXPORTED for the same single consumer and the same reason as
 * `getResolvedApiBaseUrl` above: Story 4.1's upload transport must authenticate
 * IDENTICALLY to every other call, and a second copy of this assembly would be
 * a second thing to forget when auth changes.
 */
export const buildApiHeaders = async (
  extra: Record<string, string> = {},
): Promise<Record<string, string>> => {
  const authService = getAuthService();
  const jwtToken = await authService.getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extra,
  };
  if (REACT_APP_API_TOKEN) {
    headers['x-api-token'] = REACT_APP_API_TOKEN;
  }
  if (jwtToken) {
    headers.Authorization = `Bearer ${jwtToken}`;
  }
  return headers;
};

/**
 * Generic API error handling
 */
export class ApiError extends Error {
  public status: number;
  /**
   * The machine-readable `code` from the error body, when the endpoint sends one.
   *
   * Optional and additive: routes that only send `{ error }` leave it undefined and every
   * existing caller is unaffected. It exists so a caller can tell one 409 from another
   * rather than inferring meaning from a status code an intermediary could also produce
   * (e.g. `DELIVERABLES_STALE_BASE`).
   */
  public code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = 'ApiError';
  }
}

/**
 * Authenticated API GETs must not reuse a 304. Express sends ETags; webpack
 * can forward 304 with an empty body. `response.ok` is false for 304, so Bind
 * treats impersonation session lookup as failed and skips the POST on web.
 */
async function fetchNoStore(url: string, init: RequestInit): Promise<Response> {
  const response = await globalThis.fetch(url, { ...init, cache: 'no-store' });
  if (response.status !== 304) {
    return response;
  }
  return globalThis.fetch(url, { ...init, cache: 'reload' });
}

/**
 * Generic API request function with error handling and upload progress support
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit & {
    timeout?: number;
    onUploadProgress?: (progress: number) => void;
    fileSize?: number;
  } = {},
): Promise<T> {
  try {
    /**
     * Strip app-only options before calling `fetch`: `fetch` only accepts RequestInit.
     * Spreading `options` would pass `timeout`, `onUploadProgress`, etc. and can break RN / web runtimes.
     */
    const {
      timeout: customTimeout,
      onUploadProgress,
      fileSize: uploadFileSize,
      ...fetchInit
    } = options;

    // Content type, the environment's API token, and the caller's JWT. Hoisted
    // into `buildApiHeaders` above so Story 4.1's upload transport can send the
    // SAME headers rather than assembling a second, drifting copy.
    const headers = await buildApiHeaders(
      (fetchInit.headers as Record<string, string>) || {},
    );

    // Use XMLHttpRequest for upload progress tracking on web (real progress events)
    if (onUploadProgress && Platform.OS === 'web') {
      return new Promise<T>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const timeoutMs = customTimeout || 300000; // 5 minutes default for uploads

        // Set timeout
        xhr.timeout = timeoutMs;

        if (__DEV__) {
          console.log(`📤 Starting XHR upload:`, {
            endpoint,
            timeout: timeoutMs,
            bodySize: fetchInit.body ? (fetchInit.body as string).length : 0,
            bodySizeMB: fetchInit.body
              ? Math.round((fetchInit.body as string).length / 1024 / 1024)
              : 0,
          });
        }

        // Track upload progress
        xhr.upload.addEventListener('progress', event => {
          if (event.lengthComputable) {
            const percentComplete = Math.round(
              (event.loaded / event.total) * 100,
            );
            onUploadProgress!(percentComplete);
          }
        });

        // Handle completion
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result =
                xhr.status === 204 ? {} : JSON.parse(xhr.responseText);
              resolve(result as T);
            } catch (error) {
              reject(new ApiError(xhr.status, 'Invalid JSON response'));
            }
          } else {
            let errorMessage: string;
            try {
              const errorJson = JSON.parse(xhr.responseText);
              errorMessage =
                errorJson.error || errorJson.message || `HTTP ${xhr.status}`;
            } catch {
              errorMessage = xhr.responseText || `HTTP ${xhr.status}`;
            }
            reject(new ApiError(xhr.status, errorMessage));
          }
        });

        // Handle errors
        xhr.addEventListener('error', event => {
          console.error('❌ XHR Error Event:', {
            endpoint,
            readyState: xhr.readyState,
            status: xhr.status,
            statusText: xhr.statusText,
            responseText: xhr.responseText?.substring(0, 500),
            event,
          });
          reject(
            new ApiError(
              0,
              'Network error - please check your connection and server status',
            ),
          );
        });

        xhr.addEventListener('timeout', () => {
          console.error('⏱️ XHR Timeout:', {
            endpoint,
            timeout: timeoutMs,
            readyState: xhr.readyState,
          });
          reject(new ApiError(0, 'Request timeout - upload took too long'));
        });

        // Open connection
        xhr.open(
          fetchInit.method || 'GET',
          `${getResolvedApiBaseUrl()}${endpoint}`,
          true,
        );

        // Set headers
        for (const [headerName, headerValue] of Object.entries(headers)) {
          xhr.setRequestHeader(headerName, headerValue);
        }
        xhr.setRequestHeader('Cache-Control', 'no-store');

        // Set credentials
        xhr.withCredentials = true;

        // Send request
        xhr.send(fetchInit.body as string);
      });
    }

    // Use fetch() with progress estimation for native platforms (iOS/Android)
    if (onUploadProgress && Platform.OS !== 'web') {
      return new Promise<T>(async (resolve, reject) => {
        const controller = new AbortController();
        const timeoutMs = customTimeout || 300000; // 5 minutes default for large uploads
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        // Progress estimation for native platforms
        const startTime = Date.now();
        // Conservative upload speed estimate: 500KB/s accounts for Base64 overhead and network conditions
        const estimatedSpeed = 500000; // bytes per second
        const fileSize = uploadFileSize || 1000000; // Default 1MB if not provided
        const estimatedDuration = (fileSize / estimatedSpeed) * 1000; // milliseconds

        if (__DEV__) {
          console.log(
            `📤 Starting native upload with progress estimation: ${Math.round(
              fileSize / 1024 / 1024,
            )}MB, estimated ${Math.round(estimatedDuration / 1000)}s`,
          );
        }

        // Update progress every second based on elapsed time
        const progressInterval = setInterval(() => {
          const elapsed = Date.now() - startTime;
          // Cap at 95% until actual completion to avoid misleading "100%" before done
          const estimatedProgress = Math.min(
            95,
            Math.round((elapsed / estimatedDuration) * 100),
          );
          onUploadProgress!(estimatedProgress);
        }, 1000);

        try {
          const response = await fetchNoStore(
            `${getResolvedApiBaseUrl()}${endpoint}`,
            {
              ...fetchInit,
              headers,
              credentials: 'include',
              signal: controller.signal,
            },
          );

          clearTimeout(timeoutId);
          clearInterval(progressInterval);

          // Jump to 100% on successful completion
          onUploadProgress!(100);

          if (!response.ok) {
            const errorText = await response.text();
            let errorMessage: string;

            try {
              const errorJson = JSON.parse(errorText);
              errorMessage =
                errorJson.error ||
                errorJson.message ||
                `HTTP ${response.status}`;
            } catch {
              errorMessage = errorText || `HTTP ${response.status}`;
            }

            console.error('❌ API Request Failed:', {
              endpoint,
              status: response.status,
              statusText: response.statusText,
              errorMessage,
              errorText: errorText.substring(0, 500),
            });

            throw new ApiError(response.status, errorMessage);
          }

          // Handle empty responses (like 204 No Content)
          if (response.status === 204) {
            return resolve({} as T);
          }

          const result = await response.json();
          resolve(result);
        } catch (error) {
          clearTimeout(timeoutId);
          clearInterval(progressInterval);

          if (error instanceof ApiError) {
            reject(error);
            return;
          }

          // Handle timeout/abort errors
          if (error instanceof Error && error.name === 'AbortError') {
            reject(
              new ApiError(0, 'Request timeout - server may be unavailable'),
            );
            return;
          }

          // Network or parsing errors
          if (error instanceof TypeError) {
            reject(
              new ApiError(
                0,
                'Network error - please check your connection and ensure the server is running',
              ),
            );
            return;
          }

          reject(
            new ApiError(
              0,
              error instanceof Error ? error.message : 'Unknown error occurred',
            ),
          );
        }
      });
    }

    // Fallback to fetch for requests without progress tracking or on native platforms
    const controller = new AbortController();
    // iOS needs more time due to slower network emulation - use 60s for iOS, 30s for others
    const defaultTimeout = Platform.OS === 'ios' ? 60000 : 30000;
    const timeoutMs = customTimeout || defaultTimeout;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetchNoStore(
      `${getResolvedApiBaseUrl()}${endpoint}`,
      {
        ...fetchInit,
        headers,
        credentials: 'include',
        signal: controller.signal,
      },
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage: string;

      let errorDetails: string | undefined;
      let errorCode: string | undefined;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage =
          errorJson.error || errorJson.message || `HTTP ${response.status}`;
        if (
          typeof errorJson.details === 'string' &&
          errorJson.details.trim() !== ''
        ) {
          errorDetails = errorJson.details;
        }
        if (
          typeof errorJson.code === 'string' &&
          errorJson.code.trim() !== ''
        ) {
          errorCode = errorJson.code;
        }
      } catch {
        errorMessage = errorText || `HTTP ${response.status}`;
      }

      // Enhanced error logging for debugging
      console.error('❌ API Request Failed:', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        errorMessage,
        ...(errorDetails !== undefined ? { details: errorDetails } : {}),
        errorText: errorText.substring(0, 500), // First 500 chars
      });

      throw new ApiError(
        response.status,
        errorDetails !== undefined
          ? `${errorMessage} (${errorDetails})`
          : errorMessage,
        errorCode,
      );
    }

    // Handle empty responses (like 204 No Content)
    if (response.status === 204) {
      return {} as T;
    }

    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Handle timeout/abort errors
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(0, 'Request timeout - server may be unavailable');
    }

    // Network or parsing errors
    if (error instanceof TypeError) {
      throw new ApiError(
        0,
        'Network error - please check your connection and ensure the server is running',
      );
    }

    throw new ApiError(
      0,
      error instanceof Error ? error.message : 'Unknown error occurred',
    );
  }
}

export const api = {
  get: <T>(endpoint: string, options?: { timeout?: number }) =>
    apiRequest<T>(endpoint, { method: 'GET', ...options }),

  post: <T>(
    endpoint: string,
    data: unknown,
    options?: {
      timeout?: number;
      onUploadProgress?: (progress: number) => void;
      fileSize?: number;
    },
  ) =>
    apiRequest<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    }),

  put: <T>(
    endpoint: string,
    data: unknown,
    options?: {
      timeout?: number;
      onUploadProgress?: (progress: number) => void;
      fileSize?: number;
    },
  ) =>
    apiRequest<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    }),

  patch: <T>(endpoint: string, data: unknown, options?: { timeout?: number }) =>
    apiRequest<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...options,
    }),

  delete: <T>(endpoint: string, options?: { timeout?: number }) =>
    apiRequest<T>(endpoint, { method: 'DELETE', ...options }),
};
